import { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getNavFor } from "@/lib/nav";
import { PdfViewer } from "@/components/PdfViewer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { FileText, BookOpen, Search, CheckCircle2, Layers } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function ResourcesView() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [q, setQ] = useState("");
  const [pdfView, setPdfView] = useState(null);

  useEffect(() => { api.get("/resources").then((r) => setResources(r.data.filter((x) => x.is_active))).catch(() => {}); }, []);

  const grouped = useMemo(() => {
    const g = {};
    resources.forEach((r) => { (g[r.category] = g[r.category] || []).push(r); });
    return g;
  }, [resources]);

  const open = (r) => {
    if (r.storage_path) setPdfView({ resourceId: r.id, page: 1, title: r.title });
    else navigate(`/search?q=${encodeURIComponent("overview from " + r.title)}`);
  };

  const showTest = user?.role !== "dispenser";

  return (
    <Layout nav={getNavFor(user)} showTestButton={showTest}>
      <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground">Reference Library</h1>
      <p className="mt-1 text-sm text-muted-foreground">All active, indexed references. Open a book to read it, or ask a question to search across every page.</p>

      <form onSubmit={(e) => { e.preventDefault(); if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`); }} className="relative mt-6 max-w-2xl">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search across all references (e.g. amoxicillin dosage)"
          className="h-12 rounded-xl pl-12 pr-28" data-testid="resources-search-input" />
        <Button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg" data-testid="resources-search-button">Search</Button>
      </form>

      <div className="mt-8 space-y-8">
        {Object.keys(grouped).length === 0 && <p className="text-sm text-muted-foreground">No active references yet.</p>}
        {Object.entries(grouped).map(([cat, list]) => (
          <div key={cat}>
            <div className="mb-3 flex items-center gap-2">
              <Layers className="h-4 w-4 text-sky-600" />
              <h2 className="font-heading text-lg font-bold text-foreground">{cat}</h2>
              <Badge variant="outline" className="text-xs">{list.length}</Badge>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((r) => (
                <Card key={r.id} className="flex flex-col justify-between p-5" data-testid={`resource-view-${r.id}`}>
                  <div>
                    <div className="flex items-start justify-between">
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950"><FileText className="h-5 w-5" /></div>
                      <Badge className="gap-1 bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950 dark:text-emerald-300 text-[10px]"><CheckCircle2 className="h-3 w-3" />Indexed</Badge>
                    </div>
                    <div className="mt-3 font-heading text-base font-bold text-foreground">{r.title}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{r.page_count} pages read &amp; understood by the assistant{r.usage_rules ? ` · ${r.usage_rules}` : ""}</div>
                  </div>
                  <Button variant="outline" className="mt-4 w-full" onClick={() => open(r)} data-testid={`open-resource-${r.id}`}>
                    <BookOpen className="mr-1.5 h-4 w-4" />{r.storage_path ? "Open & read" : "Explore via search"}
                  </Button>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>

      {pdfView && <PdfViewer open={!!pdfView} onClose={() => setPdfView(null)} resourceId={pdfView.resourceId} page={pdfView.page} title={pdfView.title} />}
    </Layout>
  );
}
